import { DOCUMENT, inject, Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';

import { filter, take } from 'rxjs';

import { SPLASH_SCREEN_OPTIONS } from './splash-screen.options';

const SPLASH_ELEMENT_ID = 'kros-app-splash';
const FADE_OUT_CLASS = 'kros-splash-screen-pulse--fade-out';
const FADE_OUT_FALLBACK_MS = 400;

/**
 * Removes the static boot splash once the first relevant router navigation completes.
 * Keeping the splash until NavigationEnd covers both the Angular bootstrap gap and any
 * route resolvers, so users never see a blank white screen before the first view renders.
 *
 * The splash markup must live in the app's index.html as:
 * ```html
 * <div id="kros-app-splash" class="kros-splash-screen-pulse"> ... </div>
 * ```
 * The `kros-splash-screen-pulse` styles come from `@kros-sk/bootstrap` theming.
 *
 * Wire it up via {@link provideSplashScreen}.
 */
@Injectable()
export class SplashScreenService {
  readonly #router = inject(Router);
  readonly #document = inject(DOCUMENT);
  readonly #options = inject(SPLASH_SCREEN_OPTIONS);

  constructor() {
    this.#waitForFirstRelevantNavigation();
  }

  #waitForFirstRelevantNavigation(): void {
    this.#router.events
      .pipe(
        filter(
          (event) =>
            (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) &&
            !this.#options.transientRoutes.some((url) => event.url.startsWith(url))
        ),
        take(1)
      )
      .subscribe(() => this.#removeSplash());
  }

  #removeSplash(): void {
    const splashElement = this.#document.getElementById(SPLASH_ELEMENT_ID);
    if (!splashElement) {
      return;
    }

    splashElement.classList.add(FADE_OUT_CLASS);

    const cleanup = (): void => splashElement.remove();
    splashElement.addEventListener('transitionend', cleanup, { once: true });
    this.#document.defaultView?.setTimeout(cleanup, FADE_OUT_FALLBACK_MS);
  }
}
